/*
PURPOSE: The one member-facing rendering of a contribution's fee breakdown, in two separate
layers: the receipt (artist contribution + GST-inclusive Platform Fee) and, beneath it, what
that fee covers.

USE CASES:
- Checkout and the coral creator (a preview of an unsettled contribution)
- Transaction history and the lifetime aggregate (a settled record, read from stored values)

DOMAIN BOUNDARIES:
- Presentational only: renders the figures it is given verbatim and computes no part of the
  split, which is produced once by the shared contribution-fee producer
- Payment processing belongs to the cost layer, never the receipt block: shown as a receipt line
  it re-implies a separate charge to the member, which it is not

ASSUMPTIONS:
- Money arrives as integer cents
- The layer boundary is carried in MARKUP, not styling — a heading and a labelled region each —
  so it survives with CSS disabled. The compliance argument rests on that separation
*/

import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  ContributionBreakdownView,
  BreakdownWithCosts,
  hasCostDetail,
  displayedTotalCents,
  coralSharePercentOfTotal,
  formatCents,
} from './feeBreakdownGuards';

interface Props {
  breakdown: ContributionBreakdownView;
  /** Heading for the receipt block. Each surface states its own, in that surface's tense. */
  receiptHeading: string;
  /** Puts the whole breakdown behind one control, for surfaces where it is supporting detail. */
  collapsible?: boolean;
  /** Editorial theming: the cost layer becomes a warm inset callout. Only for surfaces on the
   *  editorial palette — it assumes a light ground the cream reads against. */
  editorial?: boolean;
  /** Fires when the member opens or closes a disclosure. Lets a surface keep its analytics. */
  onToggle?: (open: boolean) => void;
}

function Row({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-4 ${emphasis ? 'font-semibold' : ''}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function DisclosureButton({
  label,
  open,
  controls,
  onActivate,
}: {
  label: string;
  open: boolean;
  controls: string;
  onActivate: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onActivate}
      aria-expanded={open}
      aria-controls={controls}
      className="flex w-full items-center justify-between text-left hover:opacity-100"
    >
      <span>{label}</span>
      <ChevronDown
        size={18}
        strokeWidth={1.75}
        aria-hidden="true"
        className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

/** Takes a narrowed breakdown, so the cost figures are never read off an absent field. */
function CostRows({ breakdown, editorial }: { breakdown: BreakdownWithCosts; editorial: boolean }) {
  const coralPercent = coralSharePercentOfTotal(breakdown);
  return (
    <div className={`mt-2 space-y-1 text-sm ${editorial ? 'text-editorial-muted/75' : ''}`}>
      <Row label="Payment processing" value={formatCents(breakdown.processingCents)} />
      <Row label="GST we remit" value={formatCents(breakdown.gstCents)} />
      <Row label="Net to Coral" value={formatCents(breakdown.coralShareCents)} />
      {coralPercent != null && (
        <p className="pt-2 text-xs">
          After processing{breakdown.gstCents > 0 ? ' and GST' : ''}, Coral keeps {coralPercent}% of your{' '}
          {formatCents(displayedTotalCents(breakdown))}.
        </p>
      )}
    </div>
  );
}

const COST_HEADING = 'What our fee covers';

/**
 * The receipt states GST as contained within the fee. A contribution made before wearecoral was
 * registered for GST carries none, and the qualifier would be a tax claim about money that never
 * bore one — so it is stated only where there is GST inside the fee to state.
 */
function platformFeeLabel(breakdown: ContributionBreakdownView): string {
  return hasCostDetail(breakdown) && breakdown.gstCents === 0
    ? 'Platform fee'
    : 'Platform fee (incl. GST)';
}

const ContributionFeeBreakdown: React.FC<Props> = ({
  breakdown,
  receiptHeading,
  collapsible = false,
  editorial = false,
  onToggle,
}) => {
  const reactId = useId();
  const receiptHeadingId = `${reactId}-receipt`;
  const receiptRegionId = `${reactId}-receipt-region`;
  const costHeadingId = `${reactId}-cost`;
  const costRegionId = `${reactId}-cost-region`;
  const [open, setOpen] = useState(false);

  const toggle = () => {
    // Deliberately not inside the state updater: React may invoke that twice, which would
    // double-count the analytics event.
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  const receiptRows = (
    <div className="mt-2 space-y-1 text-sm">
      <Row label="Artists" value={formatCents(breakdown.artistShareCents)} />
      <Row label={platformFeeLabel(breakdown)} value={formatCents(breakdown.retainedCents)} />
      <hr className="border-t border-current opacity-20 my-1" />
      <Row label="Total" value={formatCents(displayedTotalCents(breakdown))} emphasis />
    </div>
  );

  const costHeadingClass = editorial
    ? 'text-[11px] uppercase tracking-[0.18em] text-editorial-muted/55'
    : 'text-sm';

  const costSectionClass = editorial
    ? 'mt-3 rounded-2xl bg-cream px-4 py-3'
    : 'mt-3 opacity-70';

  return (
    <div>
      <section aria-labelledby={receiptHeadingId}>
        <h4 id={receiptHeadingId} className="text-sm font-medium">
          {collapsible ? (
            <DisclosureButton
              label={receiptHeading}
              open={open}
              controls={`${receiptRegionId} ${costRegionId}`}
              onActivate={toggle}
            />
          ) : (
            receiptHeading
          )}
        </h4>
        <div id={receiptRegionId} hidden={collapsible && !open}>
          {receiptRows}
        </div>
      </section>

      {/* A sibling of the receipt, never a descendant: the compliance argument rests on the two
          layers being separately-labelled regions, so nesting would silently undo it. */}
      {hasCostDetail(breakdown) && (
        <section
          id={costRegionId}
          aria-labelledby={costHeadingId}
          className={costSectionClass}
          hidden={collapsible && !open}
        >
          <h5 id={costHeadingId} className={costHeadingClass}>
            {collapsible ? (
              COST_HEADING
            ) : (
              <DisclosureButton
                label={COST_HEADING}
                open={open}
                controls={costRegionId}
                onActivate={toggle}
              />
            )}
          </h5>
          <div hidden={!collapsible && !open}>
            <CostRows breakdown={breakdown} editorial={editorial} />
          </div>
        </section>
      )}
    </div>
  );
};

export default ContributionFeeBreakdown;
